import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

import { ApiError, apiRequest } from '../../application/axios';
import { ADMINISTRATOR_API_URL } from '../../enviroments';
import { User } from '../../model/User';

export interface UserListResponse {
    data: User[];
    count: number;
}

export interface UsersState {
    byId: Map<string, User>;
    currentPage: {
        list: User[];
        count: number;
    };
    selectedUsers: User[];
    loading: boolean;
    error?: string;
}

const initialState: UsersState = {
    byId: new Map(),
    currentPage: {
        list: [],
        count: 0,
    },
    selectedUsers: [],
    loading: true,
};

interface FetchUsersParams {
    page: number;
    perPage: number;
    filter: string;
}

export const fetchUsers = createAsyncThunk<
    UserListResponse,
    FetchUsersParams,
    { rejectValue: ApiError }
>('users/fetchUsers', async ({ filter, page, perPage }, { rejectWithValue }) => {
    const response = await apiRequest<UserListResponse>({
        url: `${ADMINISTRATOR_API_URL}/v1/administrators?page=${page}&perPage=${perPage}&filter=${filter}`,
        method: 'get',
    });
    if (response instanceof ApiError) {
        return rejectWithValue(response);
    }
    return response;
});

const usersSlice = createSlice({
    name: 'users',
    initialState,
    reducers: {
        fetchUser(state, action: PayloadAction<User>) {
            const user = action.payload;
            state.byId.set(user.idpId, user);
        },
        clearUsersById(state) {
            state.byId = new Map();
        },
        clearSelectedUsers(state) {
            state.selectedUsers = [];
        },
        selectUsers(state, action: PayloadAction<User[]>) {
            state.selectedUsers = _.uniqBy(
                [...state.selectedUsers, ...action.payload],
                (user) => user.idpId,
            );
        },
        unselectUsers(state, action: PayloadAction<User[]>) {
            state.selectedUsers = _.differenceWith(state.selectedUsers, action.payload, _.isEqual);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchUsers.pending, (state) => {
                state.loading = true;
                state.error = '';
            })
            .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UserListResponse>) => {
                state.currentPage.list = action.payload.data;
                state.currentPage.count = action.payload.count;
                state.selectedUsers = [];
                state.loading = false;
            })
            .addCase(fetchUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message || 'Failed to fetch users';
            });
    },
});

export const { fetchUser, clearUsersById, clearSelectedUsers, selectUsers, unselectUsers } =
    usersSlice.actions;

export default usersSlice.reducer;
